import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    Renderer2,
    signal,
} from "@angular/core";
import { LineItemI, PricingOccurrence } from "../../../../pojo/Pricing";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { Table } from "../../../s25-table/Table";
import { S25Util } from "../../../../util/s25-util";

@TypeManagerDecorator("s25-ng-pricing-item")
@Component({
    selector: "s25-ng-pricing-item",
    template: `
        <div class="pricing-item-wrapper" [class.expand-item]="isExpandable">
            <!-- Expandable Row, ie Occurrence or Payment Breakdown -->
            @if (isExpandable) {
                <button
                    class="c-textButton occ-expand-button-{{ modelBean.charge_to_id }}"
                    [class.expanded]="toggleShowData()"
                    (click)="handleShowToggle()"
                    aria-label="Expand or Collapse Reservations"
                >
                    <s25-ng-icon [type]="'caretRight'" [ariaLabel]="false" />
                    {{ modelBean.itemName }}
                </button>
                <!-- Object Name - cannot hide row -->
            } @else if (
                (!modelBean.reservations?.length &&
                    (modelBean.total_charge || modelBean.occLineItemAmounts) &&
                    !isExpandable) ||
                (isDefined(modelBean.canHide) && !modelBean.canHide)
            ) {
                <span>{{ modelBean.itemName }}</span>
                <!-- Object Name - can hide row -->
            } @else if (
                (!modelBean.total_charge && !isExpandable && !modelBean.occLineItemAmounts) || modelBean.canHide
            ) {
                <span class="pricing-item">{{ modelBean.itemName }}</span>

                <span class="c-margin-left--quarter">
                    <s25-popover
                        [popoverTemplate]="'Items totalling $0 can be hidden'"
                        [isTooltip]="true"
                        [triggerTabIndex]="-1"
                    >
                        <s25-ng-icon-button
                            [type]="'no'"
                            [ariaLabel]="'Hide Line Item Row'"
                            (clicked)="handleRowVisibility()"
                        />
                    </s25-popover>
                </span>
            }
        </div>
    `,
    styles: `
        :host ::ng-deep .c-textButton {
            display: flex;
            padding: unset;
            text-align: left;
        }

        :host ::ng-deep .expanded {
            --icon--rotation-degrees: 90deg;
        }

        :host ::ng-deep .c-svgIcon {
            position: relative;
            bottom: 1px;
            filter: invert(33%) sepia(86%) saturate(469%) hue-rotate(160deg) brightness(101%) contrast(87%);
        }

        .line-item-name:hover {
            cursor: pointer;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25PricingItemComponent {
    @Input() modelBean: PricingItemModel;
    @Input() isExpandable: boolean;

    @Output() showData: EventEmitter<Table.NewRowModel> = new EventEmitter<Table.NewRowModel>();
    @Output() signalHiddenRows: EventEmitter<HTMLTableElement> = new EventEmitter<HTMLTableElement>();

    toggleShowData = signal(false);

    protected isDefined = S25Util.isDefined;

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
    ) {}

    handleShowToggle() {
        const tableEl = this.elementRef.nativeElement.offsetParent.offsetParent;
        const rowEl = this.elementRef.nativeElement.parentElement.parentElement;
        const rows = Array.from(tableEl.children[2].children);
        const rowIndex = rows.indexOf(rowEl);

        const tableData: Table.NewRowModel = {
            table: tableEl,
            row: rowEl,
            rowIndex: rowIndex + 1,
            action: this.toggleShowData() ? "delete" : "create",
            data: this.modelBean,
        };

        this.toggleShowData.update((prevShowData) => !prevShowData);
        this.showData.emit(tableData);
    }

    handleRowVisibility() {
        const tableEl = this.elementRef.nativeElement.offsetParent.offsetParent;
        const rowEl = this.elementRef.nativeElement.parentElement.parentElement;
        this.renderer.addClass(rowEl, "ngHidden");
        this.signalHiddenRows.emit(tableEl);
    }
}

type PricingItemModel = {
    itemName: string;
    reservations: PricingOccurrence[];
    isPayment?: boolean;
    allBillItems?: LineItemI[];
    occLineItemAmounts?: boolean;
    canHide?: boolean;
} & Pick<LineItemI, "charge_to_id" | "total_charge" | "total_tax">;
